import { getAsync, patchAsync, putAsync } from "../protect-api";
import config from "../../config";
import { apiVersion } from "../../shared/constants/common.constant";

import { useMutation, useQuery, useQueryClient } from "react-query";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type { UseMutationResult, UseQueryResult } from "react-query";
import type {
  EmailSubscriptionsResponse,
  PreferencesResponse,
  SanctionBodiesResponse,
  UpdateEmailSubscriptionsPayload,
  UpdateEmailSubscriptionsResponse,
  UpdatePreferencesPayload,
} from "./settings.types";
import type { SettingsValues } from "../../shared/types/common.type";
import { buildRolePermissionsHeaders } from "../permissions/permissions-api";
import { createQueryString } from "../../utils";

export const SANCTION_BODY_KEY = "sanctions-bodies";
export const PREFERENCES_KEY = "preferences";
export const EMAIL_SUBSCRIPTIONS_KEY = "email-subscriptions";

const getSanctionBodies = async (): Promise<SanctionBodiesResponse> => {
  const { data: sanctionBodiesResult } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/sanctionBodies`
  );

  return {
    sanctionBodies: sanctionBodiesResult.data ?? [],
  };
};

export const useGetSanctionBodies = (): UseQueryResult<
  SanctionBodiesResponse,
  ProblemDetails
> =>
  useQuery<SanctionBodiesResponse, ProblemDetails>([SANCTION_BODY_KEY], () =>
    getSanctionBodies()
  );

export const getPreferences = async (
  searchType: string,
  searchActionType: string
): Promise<PreferencesResponse> => {
  const { data } = await getAsync<PreferencesResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml?searchType=${searchType}&searchActionType=${searchActionType}`
  );

  return {
    searchType: data.searchType,
    searchActionType: data.searchActionType,
    threshold: data.threshold,
    datasets: data.datasets ?? [],
    sanctionBodies: data.sanctionBodies ?? [],
    isEmailRequired: data.isEmailRequired,
    emailRecipients: data.emailRecipients ?? [],
    pepTiers: data.pepTiers ?? [],
    dobMatching: data.dobMatching,
  };
};

export const useGetPreferences = (
  searchType: string,
  searchActionType: string,
  enabled = true
): UseQueryResult<PreferencesResponse, ProblemDetails> =>
  useQuery<PreferencesResponse, ProblemDetails>(
    [PREFERENCES_KEY, searchType, searchActionType],
    () => getPreferences(searchType, searchActionType),
    { enabled }
  );

const updatePreferences = async (
  payload: UpdatePreferencesPayload
): Promise<SettingsValues> => {
  const response = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml`,
    {
      threshold: payload.threshold,
      searchType: payload.searchType,
      searchActionType: payload.searchActionType,
      pepTiers: payload.pepTiers,
      datasets: payload.datasets,
      sanctionBodies: payload.sanctionBodies,
      isEmailRequired: payload.isEmailRequired,
      emailRecipients: payload.emailRecipients,
      dobMatching: payload.dobMatching,
    }
  );
  return response.data;
};

export const useUpdatePreferences = (): UseMutationResult<
  SettingsValues,
  ProblemDetails,
  UpdatePreferencesPayload
> =>
  useMutation<SettingsValues, ProblemDetails, UpdatePreferencesPayload>(
    updatePreferences
  );

export const getEmailSubscriptions = async (
  teamId: string,
  userId: number
): Promise<EmailSubscriptionsResponse> => {
  const { data } = await getAsync<EmailSubscriptionsResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/Settings/emailSubscriptions?${createQueryString({
      teamIds: [teamId],
      userIds: [userId],
    })}`,
    undefined,
    undefined,
    true
  );

  return data ?? [];
};

export const useGetEmailSubscriptions = (
  teamId: string,
  userId: number,
  enabled = true
): UseQueryResult<EmailSubscriptionsResponse, ProblemDetails> =>
  useQuery<EmailSubscriptionsResponse, ProblemDetails>(
    [EMAIL_SUBSCRIPTIONS_KEY, teamId, userId],
    () => getEmailSubscriptions(teamId, userId),
    { enabled: enabled && !!teamId && userId > 0 }
  );

const updateEmailSubscriptions = async (
  payload: UpdateEmailSubscriptionsPayload
): Promise<UpdateEmailSubscriptionsResponse> => {
  const { teamId, subscriptions } = payload;
  const { data } = await patchAsync<UpdateEmailSubscriptionsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/Settings/emailSubscriptions`,
    subscriptions,
    buildRolePermissionsHeaders(teamId),
    true
  );

  return {
    subscribed: data.subscribed ?? [],
    unsubscribed: data.unsubscribed ?? [],
  };
};

export const useUpdateEmailSubscriptions = (
  teamId: string,
  userId: number
): UseMutationResult<
  UpdateEmailSubscriptionsResponse,
  ProblemDetails,
  UpdateEmailSubscriptionsPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateEmailSubscriptionsResponse,
    ProblemDetails,
    UpdateEmailSubscriptionsPayload
  >(updateEmailSubscriptions, {
    onSuccess: (data) => {
      queryClient.setQueryData<EmailSubscriptionsResponse>(
        [EMAIL_SUBSCRIPTIONS_KEY, teamId, userId],
        () => [
          {
            subscribed: [...data.subscribed],
            unsubscribed: [...data.unsubscribed],
          },
        ]
      );
    },
  });
};
